import React from 'react';
import './RatingScaleDisplay.scss';
import { get } from 'lodash';

/**
 * RatingScaleDisplay Component
 * Reusable component for displaying rating scales in both preview and session contexts
 * 
 * @param {object} params - The component parameters
 * @param {number} params.minValue - Minimum value of the scale
 * @param {number} params.maxValue - Maximum value of the scale
 * @param {string} params.minLabel - Label for minimum value
 * @param {string} params.maxLabel - Label for maximum value
 * @param {number|null} params.selectedValue - Currently selected value (null for preview mode)
 * @param {function} params.onValueSelect - Callback when a value is selected (null for preview mode)
 * @param {boolean} params.isPreview - Whether this is a preview (non-interactive) display
 * @returns {JSX.Element} The rating scale display component
 */
export const RatingScaleDisplay = ({
    minValue = 1,
    maxValue = 5,
    minLabel = '',
    maxLabel = '',
    selectedValue = null,
    onValueSelect = null,
    isPreview = false
}) => {
    
    /**
     * Handles the selection of a rating value
     * @param {number} value - The selected rating value
     */
    const handleValueSelect = (value) => {
        if (!isPreview && onValueSelect) {
            onValueSelect(value);
        }
    };

    /**
     * Renders the scale points (rating buttons/numbers)
     * @returns {JSX.Element[]} Array of scale point elements
     */
    const renderScalePoints = () => {
        const points = [];
        for (let i = minValue; i <= maxValue; i++) {
            points.push(
                <div key={i} className="scale-point">
                    <div 
                        className={`scale-number ${selectedValue === i ? 'selected' : ''} ${!isPreview ? 'interactive' : ''}`}
                        onClick={() => handleValueSelect(i)}
                        role={!isPreview ? 'button' : 'presentation'}
                        tabIndex={!isPreview ? 0 : -1}
                        onKeyDown={!isPreview ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleValueSelect(i);
                            }
                        } : undefined}
                        data-testid={`rating-scale-point-${i}`}
                    >
                        {i}
                    </div>
                </div>
            );
        }
        return points;
    };

    return (
        <div className={`rating-scale-display ${isPreview ? 'preview-mode' : 'interactive-mode'}`} data-testid="rating-scale-display">
            {(minLabel || maxLabel) && (
                <div className="scale-labels" data-testid="scale-labels">
                    <span className="min-label" data-testid="min-label">{minLabel}</span>
                    <span className="max-label" data-testid="max-label">{maxLabel}</span>
                </div>
            )}
            <div className="scale-points" data-testid="scale-points">
                {renderScalePoints()}
            </div>
        </div>
    );
}; 